import { useState } from "react";
import { taxiStops, pharmacies, emergencyServices, transportLines } from "@/mocks/cityServices";

type Tab = "taxi" | "pharmacy" | "emergency" | "transport";

const tabs: { id: Tab; label: string; icon: string }[] = [
  { id: "taxi", label: "Taksi Durakları", icon: "ri-taxi-line" },
  { id: "pharmacy", label: "Eczaneler", icon: "ri-medicine-bottle-line" },
  { id: "emergency", label: "Acil Servisler", icon: "ri-hospital-line" },
  { id: "transport", label: "Ulaşım", icon: "ri-bus-line" },
];

const lineColorMap: Record<string, string> = {
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  stone: "bg-stone-100 text-stone-700 border-stone-200",
  rose: "bg-rose-100 text-rose-700 border-rose-200",
  blue: "bg-sky-100 text-sky-700 border-sky-200",
  violet: "bg-violet-100 text-violet-700 border-violet-200",
};

const DEFAULT_BURSA_MAP = "https://maps.google.com/maps?q=Bursa+Turkey+city+center&output=embed&hl=tr&z=13";

interface MapItem {
  id: number;
  name: string;
  lat: number | null;
  lng: number | null;
}

function MapPanel({ selectedItem, mapSrc }: { selectedItem: MapItem | null; mapSrc: string }) {
  return (
    <div className="relative rounded-2xl overflow-hidden border border-stone-200 bg-stone-100" style={{ height: 440 }}>
      <iframe
        key={mapSrc}
        src={mapSrc}
        title="Bursa Harita"
        className="w-full h-full border-0"
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
      {selectedItem && (
        <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2.5 border border-stone-200 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <i className="ri-map-pin-fill text-emerald-600 flex-shrink-0" />
            <span className="text-sm font-semibold text-stone-800 truncate">{selectedItem.name}</span>
          </div>
          {selectedItem.lat && (
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${selectedItem.lat},${selectedItem.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800 whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              <i className="ri-direction-line" /> Yol Tarifi
            </a>
          )}
        </div>
      )}
      {!selectedItem && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
          <div className="bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 border border-stone-100 flex items-center gap-2 whitespace-nowrap">
            <i className="ri-cursor-line text-emerald-600 text-sm" />
            <span className="text-xs text-stone-600 font-medium">Listeden bir yer seçin</span>
          </div>
        </div>
      )}
    </div>
  );
}

function TaxiTab({ showMap }: { showMap: boolean }) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selected = taxiStops.find((s) => s.id === selectedId) ?? null;
  const mapSrc = selected
    ? `https://maps.google.com/maps?q=${selected.lat},${selected.lng}&output=embed&hl=tr&z=17`
    : DEFAULT_BURSA_MAP;

  return (
    <div className={showMap ? "flex gap-5" : ""}>
      <div className={showMap ? "w-80 flex-shrink-0 space-y-3 overflow-y-auto" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"} style={showMap ? { maxHeight: 440 } : {}}>
        {taxiStops.map((stop) => (
          <div
            key={stop.id}
            onClick={showMap ? () => setSelectedId(stop.id === selectedId ? null : stop.id) : undefined}
            className={`bg-white rounded-xl border p-4 transition-all ${
              showMap ? "cursor-pointer hover:border-amber-300" : ""
            } ${selectedId === stop.id ? "border-amber-400 bg-amber-50/30" : "border-stone-100"}`}
          >
            <div className="flex items-start gap-2 mb-2">
              <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-taxi-line text-amber-600" />
              </div>
              {showMap && selectedId === stop.id && (
                <span className="ml-auto text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">Seçili</span>
              )}
            </div>
            <h4 className="font-semibold text-stone-800 text-sm mb-1">{stop.name}</h4>
            <p className="text-xs text-stone-500 mb-3 flex items-start gap-1">
              <i className="ri-map-pin-line mt-0.5 flex-shrink-0" /> {stop.address}
            </p>
            <a
              href={`tel:${stop.phone}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:text-amber-800 transition-colors cursor-pointer"
            >
              <i className="ri-phone-line" /> {stop.phone}
            </a>
          </div>
        ))}
      </div>
      {showMap && (
        <div className="flex-1 min-w-0">
          <MapPanel selectedItem={selected ? { id: selected.id, name: selected.name, lat: selected.lat, lng: selected.lng } : null} mapSrc={mapSrc} />
        </div>
      )}
    </div>
  );
}

function PharmacyTab() {
  const [filter, setFilter] = useState<"all" | "duty">("duty");
  const shown = filter === "duty" ? pharmacies.filter((p) => p.isOnDuty) : pharmacies;
  return (
    <div>
      <div className="flex gap-2 mb-5">
        {([["duty", "Nöbetçi Eczaneler"], ["all", "Tüm Eczaneler"]] as const).map(([val, label]) => (
          <button
            key={val}
            onClick={() => setFilter(val)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all ${
              filter === val ? "bg-stone-800 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map((ph) => (
          <div key={ph.id} className="bg-white rounded-xl border border-stone-100 p-4 hover:border-rose-200 transition-all">
            <div className="flex items-start justify-between mb-3">
              <div className="w-9 h-9 rounded-lg bg-rose-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-medicine-bottle-line text-rose-600" />
              </div>
              {ph.isOnDuty && (
                <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full inline-block" /> Nöbetçi
                </span>
              )}
            </div>
            <h4 className="font-semibold text-stone-800 text-sm mb-1">{ph.name}</h4>
            <p className="text-xs text-stone-500 mb-1 flex items-start gap-1">
              <i className="ri-map-pin-line mt-0.5 flex-shrink-0" /> {ph.address}
            </p>
            <p className="text-xs text-stone-400 mb-3 flex items-center gap-1">
              <i className="ri-time-line" /> {ph.hours}
            </p>
            <a
              href={`tel:${ph.phone}`}
              className="flex items-center gap-1.5 text-xs font-medium text-rose-700 hover:text-rose-800 transition-colors cursor-pointer"
            >
              <i className="ri-phone-line" /> {ph.phone}
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmergencyTab({ showMap }: { showMap: boolean }) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selected = emergencyServices.find((s) => s.id === selectedId) ?? null;
  const mapSrc = selected && selected.lat
    ? `https://maps.google.com/maps?q=${selected.lat},${selected.lng}&output=embed&hl=tr&z=16`
    : DEFAULT_BURSA_MAP;

  const iconMap: Record<string, string> = {
    Hastane: "ri-hospital-line",
    Acil: "ri-heart-pulse-line",
    İtfaiye: "ri-fire-line",
    Polis: "ri-shield-star-line",
  };
  const colorMap: Record<string, string> = {
    Hastane: "bg-blue-50 text-blue-600",
    Acil: "bg-red-50 text-red-600",
    İtfaiye: "bg-orange-50 text-orange-600",
    Polis: "bg-indigo-50 text-indigo-600",
  };

  return (
    <div className={showMap ? "flex gap-5" : ""}>
      <div className={showMap ? "w-80 flex-shrink-0 space-y-3 overflow-y-auto" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"} style={showMap ? { maxHeight: 440 } : {}}>
        {emergencyServices.map((svc) => (
          <div
            key={svc.id}
            onClick={showMap && svc.lat ? () => setSelectedId(svc.id === selectedId ? null : svc.id) : undefined}
            className={`bg-white rounded-xl border p-4 transition-all ${
              showMap && svc.lat ? "cursor-pointer hover:border-red-200" : ""
            } ${selectedId === svc.id ? "border-red-300 bg-red-50/20" : "border-stone-100"}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${colorMap[svc.type] ?? "bg-stone-50 text-stone-600"}`}>
                <i className={iconMap[svc.type] ?? "ri-map-pin-line"} />
              </div>
              <span className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                {svc.type}
              </span>
            </div>
            <h4 className="font-semibold text-stone-800 text-sm mb-1">{svc.name}</h4>
            {svc.address && (
              <p className="text-xs text-stone-500 mb-1 flex items-start gap-1">
                <i className="ri-map-pin-line mt-0.5 flex-shrink-0" /> {svc.address}
              </p>
            )}
            {svc.distance && (
              <p className="text-xs text-stone-400 mb-3 flex items-center gap-1">
                <i className="ri-route-line" /> {svc.distance}
              </p>
            )}
            <a
              href={`tel:${svc.phone}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 text-sm font-bold text-red-600 hover:text-red-700 transition-colors cursor-pointer"
            >
              <i className="ri-phone-fill" /> {svc.phone}
            </a>
          </div>
        ))}
      </div>
      {showMap && (
        <div className="flex-1 min-w-0">
          <MapPanel
            selectedItem={selected && selected.lat ? { id: selected.id, name: selected.name, lat: selected.lat, lng: selected.lng } : null}
            mapSrc={mapSrc}
          />
        </div>
      )}
    </div>
  );
}

function TransportTab() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {transportLines.map((line) => (
        <div key={line.id} className="bg-white rounded-xl border border-stone-100 p-4 hover:border-stone-200 transition-all">
          <div className="flex items-center gap-3 mb-3">
            <span className={`text-sm font-bold px-3 py-1 rounded-lg border ${lineColorMap[line.color] ?? "bg-stone-100 text-stone-700 border-stone-200"}`}>
              {line.line}
            </span>
            <div>
              <h4 className="font-semibold text-stone-800 text-sm">{line.name}</h4>
              <p className="text-xs text-stone-400 flex items-center gap-1">
                <i className="ri-time-line" /> {line.operatingHours}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-wrap mt-2">
            {line.stops.map((stop, i) => (
              <span key={i} className="flex items-center gap-1">
                <span className="text-xs text-stone-600 bg-stone-50 px-2 py-0.5 rounded-md border border-stone-100">{stop}</span>
                {i < line.stops.length - 1 && <i className="ri-arrow-right-s-line text-stone-300 text-xs" />}
              </span>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-stone-50 flex items-center gap-1 text-xs text-stone-400">
            <i className="ri-refresh-line" /> Her <strong className="text-stone-600">{line.frequency}</strong>&apos;da bir sefer
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CityServicesSection() {
  const [activeTab, setActiveTab] = useState<Tab>("taxi");
  const [showMap, setShowMap] = useState(false);

  const mapSupportedTabs: Tab[] = ["taxi", "emergency"];
  const isMapSupported = mapSupportedTabs.includes(activeTab);

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    if (!mapSupportedTabs.includes(tab)) setShowMap(false);
  };

  return (
    <section className="py-14 bg-stone-50/60">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-stone-800 tracking-tight">Şehir Hizmetleri</h2>
          <p className="text-stone-500 text-sm mt-1">Bursa&apos;da ihtiyacınız olan tüm pratik bilgiler</p>
        </div>

        {/* Tabs Row + Map Toggle */}
        <div className="flex flex-wrap items-center gap-2 mb-8 justify-between">
          <div className="flex gap-2 flex-wrap">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-stone-800 text-white"
                    : "bg-white text-stone-600 hover:bg-stone-100 border border-stone-200"
                }`}
              >
                <i className={tab.icon} />
                {tab.label}
              </button>
            ))}
          </div>

          {isMapSupported && (
            <button
              onClick={() => setShowMap((v) => !v)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                showMap
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "bg-white text-stone-600 border-stone-200 hover:border-emerald-400 hover:text-emerald-700"
              }`}
            >
              <i className={showMap ? "ri-list-check" : "ri-map-pin-2-line"} />
              {showMap ? "Liste Görünümü" : "Haritada Gör"}
            </button>
          )}
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === "taxi" && <TaxiTab showMap={showMap} />}
          {activeTab === "pharmacy" && <PharmacyTab />}
          {activeTab === "emergency" && <EmergencyTab showMap={showMap} />}
          {activeTab === "transport" && <TransportTab />}
        </div>
      </div>
    </section>
  );
}
